import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from "sonner";
import { Trophy, Award, Printer, Download, Pencil, CheckCircle, X, RotateCcw } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const PointsPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    fetchPlaydays();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchPoints();
    }
  }, [selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchPoints = async () => {
    try {
      const response = await axios.get(`${API}/results/points/${selectedPlayday}`);
      setPoints(response.data);
    } catch (error) {
      toast.error("Failed to fetch points");
    }
  };

  const getSelectedPlaydayName = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday ? `${playday.name} (${playday.date})` : "";
  };

  const totalPoints = points.reduce((sum, p) => sum + p.total_points, 0);

  const startEdit = (contestant) => {
    setEditingId(contestant.contestant_id);
    setEditValue(String(contestant.total_points));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (contestantId) => {
    try {
      await axios.put(`${API}/results/points/${selectedPlayday}/${contestantId}/override`, {
        total_points: parseInt(editValue)
      });
      toast.success("Points updated");
      setEditingId(null);
      fetchPoints();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update points");
    }
  };

  const removeOverride = async (contestantId) => {
    try {
      await axios.delete(`${API}/results/points/${selectedPlayday}/${contestantId}/override`);
      toast.success("Reverted to calculated points");
      fetchPoints();
    } catch (error) {
      toast.error("Failed to remove override");
    }
  };

  const handleExportPoints = () => {
    const exportData = points.map((contestant, idx) => ({
      rank: idx + 1,
      contestant: contestant.contestant_name,
      events: contestant.events.map(e => `${e.event_name}: ${e.points}pts`).join("; "),
      total_points: contestant.total_points
    }));
    
    exportToCSV(
      exportData, 
      `points_${getSelectedPlaydayName().replace(/[^a-z0-9]/gi, '_')}.csv`,
      ["rank", "contestant", "events", "total_points"]
    );
    toast.success("Points exported to CSV");
  };

  const handlePrintPoints = () => {
    handlePrint("Points Report", getSelectedPlaydayName());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="points-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Points</h1>
          <p className="text-text-secondary mt-1">Points earned by members for selected play day</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintPoints} disabled={points.length === 0} data-testid="print-points-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExportPoints} disabled={points.length === 0} data-testid="export-points-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Points Report</h1>
        <p>{getSelectedPlaydayName()}</p>
        <p className="mt-2">Total Points Awarded: {totalPoints}</p>
      </div>

      {/* Selector */}
      <div className="flex gap-4 items-end no-print">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Card className="border-accent/30 flex-1 max-w-xs no-print">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-accent/20 text-accent">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Total Points Awarded</p>
                <p className="font-heading font-bold text-xl text-accent">{totalPoints}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Points Table */}
      <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading font-bold text-text-primary w-16">Rank</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Contestant</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Events</TableHead>
              <TableHead className="font-heading font-bold text-text-primary text-right">Total Points</TableHead>
              <TableHead className="font-heading font-bold text-text-primary w-20 no-print">Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {points.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-text-secondary">
                  <Trophy className="h-8 w-8 mx-auto mb-2 text-text-muted" />
                  No points recorded for this play day
                </TableCell>
              </TableRow>
            ) : (
              points.map((contestant, idx) => (
                <TableRow key={contestant.contestant_id} className="hover:bg-surface-muted" data-testid={`points-${contestant.contestant_id}`}>
                  <TableCell className="font-heading font-bold text-lg text-primary">{idx + 1}</TableCell>
                  <TableCell className="font-medium text-text-primary">{contestant.contestant_name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      {contestant.events.map((e, i) => (
                        <span key={i} className="px-2 py-1 bg-accent/10 text-text-primary rounded text-xs">
                          {e.event_name}: {e.points}pts
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {editingId === contestant.contestant_id ? (
                      <div className="flex items-center justify-end gap-1">
                        <Input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 h-8 text-right font-bold"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit(contestant.contestant_id);
                            if (e.key === "Escape") cancelEdit();
                          }}
                          data-testid={`edit-points-input-${contestant.contestant_id}`}
                        />
                        <Button size="sm" className="h-8 w-8 p-0" onClick={() => saveEdit(contestant.contestant_id)} data-testid={`save-points-${contestant.contestant_id}`}>
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={cancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <span className={`font-heading font-bold text-lg ${contestant.is_overridden ? "text-status-warning" : "text-accent"}`}>
                        {contestant.total_points}
                        {contestant.is_overridden && <span className="text-xs ml-1">(edited)</span>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="no-print">
                    {editingId !== contestant.contestant_id && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-text-muted hover:text-primary"
                          onClick={() => startEdit(contestant)}
                          data-testid={`edit-points-btn-${contestant.contestant_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {contestant.is_overridden && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-text-muted hover:text-status-error"
                            onClick={() => removeOverride(contestant.contestant_id)}
                            title="Revert to calculated points"
                            data-testid={`revert-points-${contestant.contestant_id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PointsPage;
